import { graphql } from '@apollo/client/react/hoc';
import update from 'immutability-helper';

import FeedQuery from 'buyside/graphql/queries/Feed';
import FundProfileRecommendationsQuery from 'buyside/graphql/queries/FundProfileRecommendations';
import AllocatorProfileRecommendationsQuery from 'buyside/graphql/queries/AllocatorProfileRecommendations';
import CreatePostMutation from 'buyside/graphql/mutations/CreatePost';
import UpdateFundProfileMutation from 'buyside/graphql/mutations/UpdateFundProfile';
import UpdateFundProfileSectionVisibilityMutation from 'buyside/graphql/mutations/UpdateFundProfileSectionVisibility';

import FeedItemsPanel from 'buyside/components/presentation/Feed/FeedItemsPanel/feedItemsPanel';

import { flowRight as compose } from 'buyside/util/lodashAlt';

//http://dev.apollodata.com/react/pagination.html#relay-cursors
const withData = graphql(FeedQuery, {
  options: ({ feedId }) => ({
    errorPolicy: 'all',
    variables: { feedId }
  }),
  props({ data: { loading, feedItems, fetchMore, error }, ownProps: { feedId } }) {
    return {
      loading,
      feedItems,
      feedId,

      error,
      loadMoreEntries: () => {
        return fetchMore({
          query: FeedQuery,
          variables: {
            cursor: feedItems.pageInfo.endCursor,
            feedId: feedId
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.feedItems.edges;
            const pageInfo = fetchMoreResult.feedItems.pageInfo;

            const mostRecentID =
              !!previousResult.feedItems.edges.length &&
              previousResult.feedItems.edges[previousResult.feedItems.edges.length - 1].node.id;

            if (
              newEdges[newEdges.length - 1] &&
              newEdges[newEdges.length - 1].node.id === mostRecentID
            ) {
              //checks for duplicates
              return previousResult;
            }
            return {
              // Put the new comments at the end of the list and update `pageInfo`
              // so we have the new `endCursor` and `hasNextPage` values
              feedItems: {
                __typename: previousResult.feedItems.__typename,
                edges: [...previousResult.feedItems.edges, ...newEdges],
                pageInfo
              }
            };
          }
        });
      }
    };
  }
});

const withFundRecommendations = graphql(FundProfileRecommendationsQuery, {
  props({ data }) {
    const { fundRecommendations, loading, error } = data;
    return {
      fundRecommendations,
      fundRecommendationsLoading: loading,
      fundRecommendationsError: error
    };
  }
});

const withAllocatorRecommendations = graphql(AllocatorProfileRecommendationsQuery, {
  props({ data }) {
    const { allocatorRecommendations, loading, error } = data;
    return {
      allocatorRecommendations,
      allocatotRecommendationsLoading: loading,
      allocatorRecommendationsError: error
    };
  }
});

//http://dev.apollodata.com/react/mutations.html#calling-mutations
const withCreatePost = graphql(CreatePostMutation, {
  props: ({ mutate }) => ({
    handlePost: props => {
      const { body, ogUrl, files, asAdmin, tagList } = props;
      const uploadedFiles = files.map(file => {
        const { name, base64 } = file;
        const fileData = file.file;
        return {
          name,
          fileName: fileData.name,
          size: fileData.size,
          data: base64
        };
      });

      const postFiles = JSON.stringify(uploadedFiles);
      return mutate({
        variables: { body, ogUrl, postFiles, asAdmin, tagList },
        updateQueries: {
          Feed: (prev, { mutationResult }) => {
            const feedItem = mutationResult.data.createPost.feedItem;
            //required to conform to relay query spec, for some reason Apollo freaks out when it's just "FeedItem", which is what is returned by the graphql-ruby mutation
            return update(prev, {
              feedItems: {
                edges: {
                  $unshift: [{ node: feedItem, __typename: 'FeedItemEdge' }]
                }
              }
            });
          }
        }
      }).then(() => {
        window.scrollTo(0, 0);
      });
    }
  })
});

const withUpdateFundProfile = graphql(UpdateFundProfileMutation, {
  props: ({ mutate }) => ({
    handleUpdateFundProfile: ({ fundProfile, section }) => {
      const { returns, returnsUpdatedDate, returnsDocuments } = fundProfile;
      return mutate({
        variables: {
          employerProfileId: fundProfile.id,
          section,
          returns: JSON.stringify(returns),
          returnsUpdatedDate,
          returnsDocuments: JSON.stringify(returnsDocuments)
        }
      });
    }
  })
});

const withUpdateSectionVisibility = graphql(UpdateFundProfileSectionVisibilityMutation, {
  props: ({ mutate, ownProps: { employerProfileId } }) => ({
    updateSectionVisibility: (section, sectionVisibility) => {
      return mutate({
        variables: { employerProfileId, section, sectionVisibility }
      });
    }
  })
});

const FeedItemsPanelContainer = compose(
  withData,
  withFundRecommendations,
  withAllocatorRecommendations,
  withCreatePost,
  withUpdateFundProfile,
  withUpdateSectionVisibility
)(FeedItemsPanel);

export default FeedItemsPanelContainer;
